import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { formatAmount, formatTryFor0 } from 'utils/formatting';
import Button from 'design-system/Button';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import { getThemeGradient } from 'features/premium/colors';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import Links, { knownLinks } from 'utils/links';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectDefaultCurrency } from 'reducers/selectors';
import { differenceInDays, startOfDay } from 'date-fns';
import { tierGreaterThan, TierPeriod, } from '../entities';
import { selectBlinkistBackFloMonthly, selectBlinkistBackTryFor0, selectBlinkistBackV4, selectBlinkistBackV4Monthly, selectEligibleFreeTrialDays, selectIsPlatformPaySupported, selectShouldSeePremiumTrialScreen, selectShouldShowStripe, } from '../selectors';
import { getAnnualDiscountPercentage } from '../annualDiscount';
import { tryToRoundToZeroPointNinetyNine } from '../tryToRoundToZeroPointNinetyNine';
import ApplePayLogo from './ApplePayLogo';
import GooglePayLogo from './GooglePayLogo';
import { selectIsUserOnPremium, selectUserTier } from '../selectors/tier';
import { tierThemes } from '../benefits';
import usePremiumPlanChangeExperience from '../hooks/usePremiumPlanChangeExperience';
const getMonthlyPrice = (subscription) => {
    switch (subscription.period) {
        case TierPeriod.yearly:
            return `${formatAmount(tryToRoundToZeroPointNinetyNine(parseFloat(subscription.price) / 12), subscription.currency)}/month, renews annually`;
        default:
            return `${formatAmount(parseFloat(subscription.price), subscription.currency)}/month`;
    }
};
const getYearlyPrice = (subscription) => {
    switch (subscription.period) {
        case TierPeriod.monthly:
            return formatAmount(parseFloat(subscription.price) * 12, subscription.currency);
        default:
            return formatAmount(parseFloat(subscription.price), subscription.currency);
    }
};
const PeriodSelectionModal = ({ reopen, defaultSelection, subscriptions, onSelect, onClose, }) => {
    const navigation = useMainStackNavigation();
    const shouldSeePremiumTrialScreen = useAppSelector((store) => selectShouldSeePremiumTrialScreen(store, subscriptions[0].tier));
    const blinkistBackFloMonthly = useAppSelector(selectBlinkistBackFloMonthly);
    const blinkistBackV4Monthly = useAppSelector(selectBlinkistBackV4Monthly);
    // The `flo_preselected_monthly` / `v4_preselected_monthly` variants pre-select monthly instead of
    // yearly, but only within the onboarding trial paywall — plan changes and
    // other flows keep the yearly default.
    const preSelectMonthly = (blinkistBackFloMonthly || blinkistBackV4Monthly) &&
        shouldSeePremiumTrialScreen;
    const monthlyIndex = subscriptions.findIndex((sub) => sub.period === TierPeriod.monthly);
    const resolvedDefaultSelection = defaultSelection ??
        (preSelectMonthly && monthlyIndex >= 0 ? monthlyIndex : 0);
    const [selection, setSelection] = useState(resolvedDefaultSelection);
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    const colors = useColors();
    const userIsOnPremium = useAppSelector(selectIsUserOnPremium);
    const userTier = useAppSelector(selectUserTier);
    const newTier = subscriptions.find((sub) => Object.prototype.hasOwnProperty.call(sub, 'tier'))?.tier;
    const isUpgrade = tierGreaterThan(newTier, userTier);
    const theme = tierThemes[userTier];
    const themeGradient = useMemo(() => getThemeGradient(subscriptions[0].tier, colors)?.colors, [subscriptions, colors]);
    const styles = useStyles(styleSet);
    const annualDiscount = useMemo(() => {
        const monthly = subscriptions.find((it) => it.period === TierPeriod.monthly);
        const yearly = subscriptions.find((it) => it.period === TierPeriod.yearly);
        return getAnnualDiscountPercentage(monthly?.price, yearly?.price);
    }, [subscriptions]);
    const data = useMemo(() => {
        if (subscriptions.length < 2)
            return [];
        const biggestPrice = getYearlyPrice(subscriptions[subscriptions.length - 1]);
        return subscriptions.map((it) => ({
            period: it.period === TierPeriod.monthly ? 'Monthly' : 'Annual -',
            oldPrice: it.period === TierPeriod.monthly ? '' : biggestPrice,
            tNew: it.period === TierPeriod.monthly ? '' : 'Now',
            newPrice: it.period === TierPeriod.monthly
                ? ''
                : `${formatAmount(parseFloat(it.price), it.currency)}/yr`,
            monthlyPrice: getMonthlyPrice(it),
        }));
    }, [subscriptions]);
    const canUseStripe = useAppSelector(selectShouldShowStripe);
    const isPlatformPaySupported = useAppSelector(selectIsPlatformPaySupported);
    const showLogo = useMemo(() => {
        if (canUseStripe && isPlatformPaySupported) {
            if (Platform.OS === 'ios')
                return <ApplePayLogo style={styles.platformPayLogo}/>;
            if (Platform.OS === 'android')
                return <GooglePayLogo style={styles.platformPayLogo}/>;
        }
        return null;
    }, [canUseStripe, isPlatformPaySupported, styles.platformPayLogo]);
    useEffect(() => {
        setSelection(resolvedDefaultSelection);
    }, [subscriptions, resolvedDefaultSelection]);
    const { startFlow, isFetching } = usePremiumPlanChangeExperience();
    const onContinue = useCallback(() => {
        if (selection >= 0) {
            return startFlow(subscriptions[selection], () => onSelect?.(subscriptions[selection]), onClose);
        }
        return null;
    }, [onClose, onSelect, selection, startFlow, subscriptions]);
    const handleTnc = useCallback(() => {
        if (Platform.OS === 'android') {
            onClose?.(selection);
            navigation.navigateDeprecated('Pdf', {
                url: knownLinks.paidPlanTncs,
                onClose: () => {
                    reopen();
                },
            });
        }
        else {
            Links.openKnownLink('paidPlanTncs');
        }
    }, [onClose, reopen, selection]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const blinkistBackTryFor0 = useAppSelector(selectBlinkistBackTryFor0);
    const blinkistBackV4 = useAppSelector(selectBlinkistBackV4);
    const showTryFor0 = ((blinkistBackTryFor0 || blinkistBackV4) && shouldSeePremiumTrialScreen) ||
        !!(shouldSeePremiumTrialScreen && isUpgrade && eligibleFreeTrialDays) ||
        !!(emmaProStatus.isOnFreeTrial &&
            emmaProStatus.currentFreeTrialDays &&
            isUpgrade);
    const remainingTrialDays = useMemo(() => {
        if (emmaProStatus.isOnFreeTrial && emmaProStatus.expiresOn) {
            return Math.max(0, differenceInDays(startOfDay(new Date(emmaProStatus.expiresOn)), startOfDay(new Date())));
        }
        return null;
    }, [emmaProStatus.expiresOn, emmaProStatus.isOnFreeTrial]);
    return (<View style={[paddingBottom, !showLogo && styles.paddingTop]}>
      {showLogo}
      <View>
        {data.map((it, index) => (<TouchableBounce onPress={() => setSelection(index)} style={styles.touchable} key={it.period}>
            {index === 0 && annualDiscount !== undefined && (<View style={[styles.bage, { backgroundColor: themeGradient?.[0] }]}>
                <Text Text-14 OnPrimary>
                  {`${annualDiscount}% OFF`}
                </Text>
              </View>)}
            <View style={[
                styles.item,
                index === selection && { borderColor: themeGradient?.[0] },
                index === selection && styles.selectedItem,
                index === 0 && { paddingTop: rem(24) },
            ]}>
              <View style={styles.textsContainer}>
                <Text Text-16>{it.period}</Text>
                <Spacer w={8}/>
                <Text Text-16 style={styles.oldPrice}>
                  {it.oldPrice}
                </Text>
                <Spacer w={8}/>
                <Text Text-16>{it.tNew}</Text>
                <Spacer w={8}/>
                <Text Text-16 color={themeGradient?.[0]}>
                  {it.newPrice}
                </Text>
              </View>
              <Text TextThin-14>{it.monthlyPrice}</Text>
            </View>
          </TouchableBounce>))}
      </View>
      <View style={styles.footer}>
        <Text style={styles.trial} TextThin-14 Secondary centered>
          {/* eslint-disable-next-line no-nested-ternary */}
          {shouldSeePremiumTrialScreen
            ? `Try ${eligibleFreeTrialDays} days free. `
            : // eslint-disable-next-line no-nested-ternary
                showTryFor0 &&
                    emmaProStatus.isOnFreeTrial &&
                    remainingTrialDays != null
                    ? `Free for your remaining ${remainingTrialDays > 1 ? `${remainingTrialDays} ` : ''}trial ${remainingTrialDays > 1 ? 'days' : 'period'}. `
                    : isUpgrade && userIsOnPremium && canUseStripe
                        ? `If you decide to upgrade, we’ll credit any unused time from Emma ${theme.shortName}, so you only pay the difference. `
                        : null}
          Cancel anytime.{' '}
          {canUseStripe ? (<Text style={styles.trial} TextThin-14 Primary suppressHighlighting onPress={handleTnc}>
              T&Cs{' '}
              <Text style={styles.trial} TextThin-14 Secondary>
                apply.
              </Text>
            </Text>) : null}
        </Text>
      </View>

      <Button custom onPress={onContinue} isFetching={isFetching} title={showTryFor0 ? formatTryFor0(userCurrency) : 'Continue'} gradientColors={themeGradient} textColor={colors.text.onPrimary}/>
    </View>);
};
PeriodSelectionModal.show = (props, options) => {
    Modal.show(<PeriodSelectionModal {...props}/>, {
        ...options,
        containerStyle: {
            paddingHorizontal: 0,
            paddingBottom: 0,
        },
    });
};
PeriodSelectionModal.hide = (onDidHideCallback, options) => {
    Modal.hide(onDidHideCallback, options);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        width: '100%',
        flex: 1,
        alignItems: 'stretch',
        paddingHorizontal: rem(16),
        backgroundColor: colors.background.light,
    },
    paddingTop: {
        paddingTop: rem(70),
    },
    platformPayLogo: {
        marginTop: rem(51),
        marginBottom: rem(19),
    },
    touchable: { marginBottom: rem(16) },
    bage: {
        alignSelf: 'flex-end',
        justifyContent: 'center',
        alignItems: 'center',
        height: rem(36),
        marginEnd: rem(24),
        borderRadius: rem(10),
        paddingHorizontal: rem(32),
        paddingVertical: rem(6),
        marginVertical: -rem(19),
        zIndex: 2,
    },
    item: {
        alignItems: 'flex-start',
        paddingHorizontal: rem(24),
        paddingBottom: rem(16),
        paddingTop: rem(16),
        marginBottom: rem(16),
        borderRadius: rem(16),
        borderColor: colors.borders.divider,
        borderWidth: rem(1),
        zIndex: 1,
    },
    selectedItem: {
        backgroundColor: colors.background.light,
        shadowRadius: 20 / 2.0,
        shadowColor: Platform.select({
            android: 'rgba(19, 48, 104, 0.4)',
            ios: '#13306812',
        }),
        shadowOpacity: 1,
        shadowOffset: { height: 10, width: 15 },
        elevation: 10,
    },
    textsContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        marginBottom: rem(4),
    },
    oldPrice: {
        textDecorationLine: 'line-through',
    },
    trial: {
        alignSelf: 'center',
        marginBottom: rem(16),
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
export default PeriodSelectionModal;
